import { useState, useEffect } from "react";
import { Bell, BellOff } from "lucide-react";
import { apiCall } from "../App";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const PushToggle = ({ token }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setSupported(false);
        setLoading(false);
        return;
      }
      setSupported(true);
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setEnabled(!!sub);
        }
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    check();
  }, []);

  const togglePush = async () => {
    if (!supported) return toast.error("Push notifications not supported in this browser");
    setLoading(true);

    try {
      if (enabled) {
        // Unsubscribe
        const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            await apiCall("POST", "/push/unsubscribe", { endpoint: sub.endpoint }, token);
            await sub.unsubscribe();
          }
        }
        setEnabled(false);
        toast.success("Push notifications disabled");
      } else {
        // Subscribe
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setLoading(false);
          return toast.error("Notification permission denied by browser");
        }

        const reg = await navigator.serviceWorker.register('/sw-push.js');
        await navigator.serviceWorker.ready;

        // Get VAPID key
        const vapidRes = await apiCall("GET", "/push/vapid-key", null, token);
        if (!vapidRes.success || !vapidRes.data.vapid_public_key) {
          setLoading(false);
          return toast.error("Push configuration not available");
        }

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidRes.data.vapid_public_key),
        });

        await apiCall("POST", "/push/subscribe", { subscription: sub.toJSON() }, token);
        setEnabled(true);
        toast.success("Push notifications enabled!");
      }
    } catch (e) {
      toast.error("Failed to toggle push notifications");
    }
    setLoading(false);
  };

  if (!supported) return null;

  return (
    <button onClick={togglePush} disabled={loading}
      className={`p-2 rounded-lg transition-all ${enabled ? "hover:bg-divine-gold/10 text-divine-gold" : "hover:bg-chakra-crown/10 text-chakra-crown/30"}`}
      title={enabled ? "Push notifications ON — click to disable" : "Enable push notifications"}
      data-testid="push-toggle-btn">
      {enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
    </button>
  );
};

export default PushToggle;
